<script>
import * as DataMappers from '../Data/Mappers';
import { isPlainObject, cloneObject } from '../../../../helpers/common';

export default {
    props: {
        value: {
            type: String | Object | Array,
            default: ''
        },
        valueName: {
            type: String
        },
        idKey: {
            type: String,
            default: 'id'
        },
        idValue: {
            type: String | Number
        },
        additionalData: {
            type: Object
        },
        dataMappers: {
            type: Array,
            default() {
                return [];
            }
        },
        bubbleChanges: {
            type: Boolean,
            default: true
        },
        // If it should ignore the next 'dataChanged' call (by user input) after new data got set (trough setData method)
        // May be needed if a plugin is used which triggers a input event after new data is set programmatically
        ignoreUserChangeAfterSet: {
            type: Boolean,
            default: false
        }
    },
    data() {
        if(this.value && this.ignoreUserChangeAfterSet) {
            this.ignoreNextOnChangedByUser = true;
        }
        
        return {
            data: this.value,
            dataAdditional: this.additionalData,
            changedByUserInput: false
        };
    },
    computed: {
        hasValue() {
            return this.data && this.data.toString().length;
        },
        formData() {
            return this.data;
        },
        formDataName() {
            return this.valueName ? this.valueName : this.baseName;
        }
    },
    created() {
        this.dataProcessing = {
            get: [ 
                data => { return this.applyDataMappers(data, 'from'); } 
            ],

            setBefore: [
                this.prepareNewData,
                this.filterDataById
            ],
            set: [ 
                this.setAdditionalData,
                this.getSubNamesData, 
                newData => { return this.applyDataMappers(newData, 'to'); },
                this.handleNewData
            ],
            setAfter: []
        };
    },
    methods: {
        addDataProcessor(processor, mode, pos = 1) {
            if(pos == 1) {
                this.dataProcessing[mode].push(processor);
            } else {
                this.dataProcessing[mode].unshift(processor);
            }
        },
        dataChanged(data = undefined, byUserInput = undefined) {
            if(typeof data == 'boolean' && byUserInput === undefined) {
                byUserInput = data;
                data = undefined;
            }

            if(!byUserInput || !this.ignoreNextOnChangedByUser) {
                this.$emit('datachange', { data: this.getData(data), source: this });
            }

            if(byUserInput) {
                if(!this.ignoreNextOnChangedByUser) {
                    this.changedByUserInput = true;
                    this.$emit('user-input', { data: this.getData(data), source: this });
                }

                this.ignoreNextOnChangedByUser = false;
            }
        },
        _getData(data = undefined) {
            data = data == undefined ? this.formData : data;

            if(this.dataAdditional) {
                if(!isPlainObject(data)) {
                    data = {};
                    data[this.formDataName] = this.formData;
                }

                data = Object.assign({}, this.dataAdditional, data);
            }

            let dataObject = {};
            let target = dataObject;

            if(this.subNames.length) {
                let subNames = this.subNames.slice(0);
                let lastSubName = subNames.pop();

                subNames.forEach(subName => {
                    target[subName] = {};
                    target = target[subName];
                });

                target[lastSubName] = data;
            } else {
                dataObject = data;
            }

            const processedData = this.processData(dataObject, 'get');

            if(!this.bubbleChanges && processedData && typeof processedData == 'object') {
                return cloneObject(processedData);
            }

            return processedData;
        },
        getData(data = undefined) {
            return this._getData(data);
        },
        _setData(newData) {
            if(!this.bubbleChanges && newData && typeof newData == 'object') {
                newData = cloneObject(newData);
            }

            newData = this.processData(newData, 'setBefore');
            this.data = this.processData(newData, 'set');
            this.processData(this.data, 'setAfter');

            return this.data;
        },
        setData(newData) {
            return this._setData(newData);
        },
        setAdditionalData(newData) {
            let data = newData;

            if(this.dataAdditional && isPlainObject(newData)) {
                data = {};

                Object.keys(newData).forEach(key => {
                    if((this.data === null || !isPlainObject(this.data) || !(key in this.data)) && (key in this.dataAdditional)) {
                        this.dataAdditional[key] = newData[key];
                    } else {
                        data[key] = newData[key];
                    }
                });
            }
    
            return data;
        },
        setAdditionalDataKey(key, data, mode = 'merge') {
            if(!key in this.dataAdditional) {
                this.$set(this.dataAdditional, key, data);
            } else {
                if(mode == 'merge') {
                    if(isPlainObject(this.dataAdditional[key]) && isPlainObject(data)) {
                        this.$set(this.dataAdditional, key, Object.assign(this.dataAdditional[key], data));
                    } else if(Array.isArray(this.dataAdditional[key]) && Array.isArray(data)) {
                        this.$set(this.dataAdditional, key, this.dataAdditional[key].concat(data));
                    } else {
                        this.$set(this.dataAdditional, key, data);
                    }
                } else if(mode == 'toggle') {
                    if(isPlainObject(this.dataAdditional[key]) && isPlainObject(data)) {
                        Object.keys(data).forEach(k => {
                            if(k in this.dataAdditional[key] && data[k] == this.dataAdditional[key]) {
                                this.$delete(this.dataAdditional, key);
                            } else {
                                this.$set(this.dataAdditional[key], k, data[k]);
                            }
                        });
                    } else if(Array.isArray(this.dataAdditional[key]) && Array.isArray(data)) {
                        for(let item of data) {
                            let itemIndex = this.dataAdditional[key].indexOf(item);

                            if(itemIndex != -1) {
                                this.dataAdditional[key].splice(itemIndex, 1);
                            } else {
                                this.dataAdditional[key].push(item);
                            }
                        }
                    } else {
                        this.$set(this.dataAdditional, key, data);
                    }
                } else {
                    this.$set(this.dataAdditional, key, data);
                }
            }
        },
        getSubNamesData(data) {
            if(isPlainObject(data) && (this.data === null || !isPlainObject(this.data))) {
                data = (this.formDataName in data) ? data[this.formDataName] : data;
                
                let found = true;

                if(isPlainObject(data) && this.subNames.length) {
                    for(var subName of this.subNames) {
                        if(subName in data) {
                            data = data[subName];
                        } else {
                            found = false;
                            break;
                        }
                    }
                }

                if(!found) {
                    data = this.value;
                }
            }

            return data;
        },
        prepareNewData(data) {
            if(Array.isArray(data) && data.length == 0) {
                return {};
            }

            return data;
        },
        handleNewData(data) {
            if(this.ignoreUserChangeAfterSet && this.data != data) {
                this.ignoreNextOnChangedByUser = true;
            }

            return data;
        },
        processData(data, mode) {
            for(var processor of this.dataProcessing[mode]) {
                data = processor.call(this, data);
            }

            return data;
        },
        applyDataMappers(data, mode) {
            for(var mapper of this.dataMappers) {
                mapper = mode + mapper[0].toUpperCase() + mapper.substr(1) + 'DataMapper';

                if(DataMappers[mapper]) {
                    data = DataMappers[mapper](data);
                }
            }

            return data;
        },
        filterDataById(newData) {
            if(this.idValue != undefined && Array.isArray(newData)) {
                for(let data of newData) {
                    if(data && typeof data == 'object' && this.idKey in data && data[this.idKey] == this.idValue) {
                        return data;
                    }
                }
            }

            return newData;
        }
    }
}
</script>